import type { CheckoutWebComponents, Component } from '@checkout.com/checkout-web-components'
import { type PaymentMethod } from '@hooks-api/types/dataModel/payment'
import { BaseCheckoutPayFlowModule, type MountTargetOverrides } from './BaseCheckoutPayFlowModule'
import { getCkoStoredCardMountContainerId } from './ckoMountTarget'
import { PAYMENT_DIALOG_TYPE, type PaymentDialogType } from './payment-dialog-type'

const resolveStoredCardValidity = (component: Component): boolean => component.isValid()

/** 一次性的「component 已 ready」訊號。新增早退路徑時只需呼叫 `clear()`，無須自行維護 promise 與 resolver。 */
class ReadySignal {
  private pending: Promise<void> | null = null
  private resolvePending: (() => void) | null = null

  public arm(): void {
    // 前一輪尚未 signal 就替換 pending，會連同 resolver 一起丟棄，等待中的呼叫端將永遠不被喚醒。
    this.signal()
    this.pending = new Promise<void>((resolve) => { this.resolvePending = resolve })
  }

  public signal(): void {
    this.resolvePending?.()
    this.resolvePending = null
  }

  /** 先喚醒等待中的呼叫端再清除 pending，避免留下永不 resolve 的 promise。 */
  public clear(): void {
    this.signal()
    this.pending = null
  }

  public wait(): Promise<void> {
    return this.pending ?? Promise.resolve()
  }
}

/**
 * Credit card module with **exclusive** card / stored_card mounting.
 *
 * When the session carries `stored_card.instrument_ids`, the saved-card selector is
 * mounted and the new-card input is skipped; otherwise the new-card input mounts as
 * a fallback. Only one component is ever live, so the UI never shows both at once.
 */
export class CheckoutPayCreditCardModule extends BaseCheckoutPayFlowModule {
  private storedCardComponent: Component | null = null
  private storedCardMountElement: string | Element | null = null
  private onValidationChangeCallbacks = new Set<(isValid: boolean) => void>()
  private storedCardReady = new ReadySignal()
  private storedCardIsValid = false

  /**
   * 卡片驗證狀態變動時通知所有監聽者，回傳取消監聽函式。
   * Footer 送出前驗證與 Dialog 送出按鈕的 disabled 判斷會同時各自監聽同一個 module。
   */
  public onValidationChange(callback: (isValid: boolean) => void): () => void {
    this.onValidationChangeCallbacks.add(callback)
    return () => {
      this.onValidationChangeCallbacks.delete(callback)
    }
  }

  private emitValidationChange(isValid: boolean): void {
    this.onValidationChangeCallbacks.forEach(callback => callback(isValid))
  }

  protected createComponent(
    checkout: CheckoutWebComponents,
    callbacks: Parameters<BaseCheckoutPayFlowModule['createComponent']>[1],
  ): Component {
    return checkout.create('card', {
      ...callbacks,
      showPayButton: false,
      onChange: (component) => {
        this.emitValidationChange(component.isValid())
      },
    })
  }

  public override mountsInDialog(): boolean {
    return true
  }

  public override getPaymentDialogType(): PaymentDialogType {
    return PAYMENT_DIALOG_TYPE.CREDIT_CARD
  }

  /** 重掛會清掉 stored_card 的選卡與 new-card 已輸入的卡號。 */
  public override shouldPreserveMountedState(): boolean {
    return this.hasStoredCardAvailable() || this.isCurrentlyValid()
  }

  protected setStoredCardMountElement(element: string | Element | null): void {
    this.storedCardMountElement = element
  }

  public override configureBeforeMount(method: PaymentMethod, overrides?: MountTargetOverrides): void {
    const containerId = overrides?.storedCardContainerId ?? getCkoStoredCardMountContainerId(method)
    this.setStoredCardMountElement(containerId ? `#${containerId}` : null)
  }

  protected override async mountInternal(element: string | Element): Promise<void> {
    const { appKey, paymentSession, locale, componentOptions } = this
    await this.bootSdk(appKey, paymentSession, locale, componentOptions)
    if (!this.checkout) return

    // 先取為區域變數：await 期間 unmount() 可能將欄位設為 null，後續掛載目標必須是進入本函式時的那一個。
    const storedCardMountElement = this.storedCardMountElement
    if (storedCardMountElement) {
      this.storedCardReady.arm()
      const storedCard = this.checkout.create('stored_card', {
        ...this.createCallbackWrappers(),
        showPayButton: false,
        onReady: (component) => {
          this.storedCardIsValid = resolveStoredCardValidity(component)
          console.debug('[CheckoutPayCreditCardModule] stored_card onReady', { selectedType: component.selectedType, selectedPaymentMethodId: component.selectedPaymentMethodId, isValid: this.storedCardIsValid })
          this.emitValidationChange(this.storedCardIsValid)
          this.storedCardReady.signal()
        },
        onChange: (component) => {
          this.storedCardIsValid = resolveStoredCardValidity(component)
          console.debug('[CheckoutPayCreditCardModule] stored_card onChange', { selectedType: component.selectedType, selectedPaymentMethodId: component.selectedPaymentMethodId, isValid: this.storedCardIsValid })
          this.emitValidationChange(this.storedCardIsValid)
        },
      })
      if (await storedCard.isAvailable()) {
        // 不記 storedCardComponent：否則 applyCkoSession 的 hasStoredCardAvailable() 早退
        // 會讓之後每次開啟都跳過 mount，dialog 永遠空白。
        if (!this.isMountContainerInDom(storedCardMountElement)) {
          console.warn('[CheckoutPayCreditCardModule] stored_card container no longer in DOM — skipping mount')
          this.storedCardReady.clear()
          this.checkout = null
          return
        }
        this.storedCardComponent = storedCard
        storedCard.mount(storedCardMountElement)
        this.recordMountTarget(storedCardMountElement)
        return
      }
      this.storedCardReady.clear()
    }

    await super.mountInternal(element)
  }

  protected override hasMountedComponent(): boolean {
    return super.hasMountedComponent() || this.storedCardComponent !== null
  }

  public override async submit(): Promise<void> {
    // 等 mount 完成 storedCardComponent 才是 final state，否則分支會錯
    await this.awaitMount()
    if (this.storedCardComponent) {
      await this.storedCardReady.wait()
      console.debug('[CheckoutPayCreditCardModule] submit stored_card', {
        isValid: this.storedCardComponent.isValid?.(),
        selectedType: this.storedCardComponent.selectedType,
        selectedPaymentMethodId: this.storedCardComponent.selectedPaymentMethodId,
      })
      this.storedCardComponent.submit()
      return
    }
    console.debug('[CheckoutPayCreditCardModule] submit new-card', { isValid: this.component?.isValid?.() })
    await super.submit()
  }

  public override unmount(): void {
    this.storedCardComponent?.unmount()
    this.storedCardComponent = null
    this.storedCardIsValid = false
    this.storedCardReady.clear()
    super.unmount()
    // 卸載後回報 false 重置監聽端的有效狀態，否則付款鈕沿用先前的 true、在空欄位上仍可送出。
    this.emitValidationChange(false)
  }

  public hasStoredCardAvailable(): boolean {
    return this.storedCardComponent !== null
  }

  public isStoredCardValid(): boolean {
    return this.storedCardIsValid
  }

  public isCurrentlyValid(): boolean {
    if (this.storedCardComponent) return this.storedCardIsValid
    return this.component?.isValid?.() ?? false
  }

}
